import { listenForVariantOptionsChanges } from '@surf-turf/nemo/assets/utils-events'
import { returnObjectOrNull, returnStringOrNull } from '@surf-turf/nemo/assets/utils-type-check'
import { handleSetImageInSizeFieldsetElements, handleSetPriceInFieldsetElement } from '../utils/inputs'
import { pricing } from '../utils/pricing'
import { getValueFromUrl } from '../utils/url'

class ConnectedProduct extends HTMLElement {
  constructor() {
    super()

    const connectedProductElement = this
    const productFormElement = document.querySelector('product-form')
    const fieldsetElement = connectedProductElement.querySelector('fieldset')
    const variantIdElement = connectedProductElement.querySelector('.variant-id')
    const variantDataJson = connectedProductElement.querySelector('.connected-product-variant-data')
    const allVariants = variantDataJson ? JSON.parse(variantDataJson.innerHTML).variants : null
    const connectedProductColorFieldsetElement = connectedProductElement.querySelector('fieldset[name="connected-product-color"]')
    const connectedProductPairingFieldsetElement = connectedProductElement.querySelector('fieldset[name="connected-product-pairing"]')
    const connectedProductSizeFieldsetElement = connectedProductElement.querySelector('property-holder.property--size fieldset')
    const defaultPairingValue = 'single'

    if (!fieldsetElement) {
      return
    }

    /**
     * Get value of fieldset.
     *
     * @param {string} fieldsetName
     *
     * @returns {string}
     */
    const getValueOfFieldset = (fieldsetName) => {
      const fieldsetElement = productFormElement.querySelector(`fieldset[name="${fieldsetName}"]`)
      const selectedInputInFieldset = fieldsetElement?.querySelector('input:checked')

      return returnStringOrNull(selectedInputInFieldset?.value)
    }

    /**
     * Get value from variant Selector.
     * Returns the selected value from a selector element.
     *
     * @param variantElement {HTMLElement}
     * @param optionName {string}
     *
     * @returns {*}
     */
    const getValueFromVariantSelectorByOptionNameOrNull = (variantElement) => {
      const checkedElement = variantElement.querySelector('input:checked')

      return returnStringOrNull(checkedElement ? checkedElement.value : null)
    }

    /**
     * Get correct variant from selected options.
     *
     * @param {array} variantOptions }
     *
     * @returns {object|null}
     */
    const getCorrectVariantFromOptionsOrNull = (variantsDataObject, selectedOptions) => {
      if (variantsDataObject != null) {
        return returnObjectOrNull(variantsDataObject.find((element) => element.options === selectedOptions))
      }
      return null
    }

    const getAllOptions = (arrayOfOptionNames) => {
      const allOptions = []

      arrayOfOptionNames.map((optionName) => {
        switch (optionName.toLowerCase()) {
          case 'size':
            // get value from size option of main product and
            const sizeValue = getValueFromVariantSelectorByOptionNameOrNull(connectedProductSizeFieldsetElement)

            if (sizeValue) {
              if (sizeValue === 'none') {
                variantIdElement.value = null
                return null
              }
              allOptions.push({ size: sizeValue })
            }
            break

          case 'color':
            // get value from color option of main product and
            const colorValue = getValueOfFieldset('color')

            if (colorValue) {
              const inputElement = connectedProductColorFieldsetElement.querySelector('input')
              // set value in connected product fieldset.
              inputElement.value = colorValue

              allOptions.push({ color: colorValue })
            }
            break

          case 'pairing':
            // get value from pairing option of main product and
            let pairingValue = getValueOfFieldset('pairing')

            if (pairingValue) {
              const inputElement = connectedProductPairingFieldsetElement.querySelector('input')
              // set value in connected product fieldset.
              inputElement.value = pairingValue

              if (pairingValue.toLowerCase() === 'left' || pairingValue.toLowerCase() === 'right') {
                pairingValue = defaultPairingValue
              } else {
                pairingValue = 'pair'
              }

              allOptions.push({ color: pairingValue })
            } else {
              // Set default to be single (silos 2pc)
              pairingValue = defaultPairingValue

              allOptions.push({ color: pairingValue })
            }
            break

          default:
            break
        }
      })

      return allOptions
    }

    /**
     * Return variant list.
     *
     * @param {array} arrayOfOptionValues
     *
     * @returns {array}
     */
    const returnVariantList = (arrayOfOptionValues) => {
      const variantDataList = []

      // Get the options for size.
      const allInputElementsOfSize = connectedProductSizeFieldsetElement.querySelectorAll('input')
      const arrayOfInputElements = [...allInputElementsOfSize]

      arrayOfInputElements.map((inputElement) => {
        const value = inputElement.value
        const parentInputElement = inputElement.closest('.input')

        if (value === 'none') {
          // Do nothing.
        } else {
          const leftRighIndex = arrayOfOptionValues.findIndex((option) => option === 'left' || option === 'right')
          const isPairingSet = arrayOfOptionValues.findIndex((option) => option === 'left' || option === 'right' || option === 'single' || option === 'single')

          if (leftRighIndex >= 0) {
            arrayOfOptionValues[leftRighIndex] = 'single'
          }

          if (isPairingSet <= 0) {
            arrayOfOptionValues.push('single')
          }

          const finalOptionString = `${value} - ${arrayOfOptionValues.join(' - ')}`
          const variantObject = getCorrectVariantFromOptionsOrNull(allVariants, finalOptionString)

          if (variantObject) {
            variantDataList.push({
              inputElement: parentInputElement,
              variantData: variantObject,
            })
          }
        }
      })

      return variantDataList
    }

    /**
     * Get all size variant data objects.
     */
    const getAllSizeVariantDataObjects = () => {
      const getAllOptionsNames = allVariants.find((variant) => variant.product_option_titles)
      const arrayOfOptionNames = getAllOptionsNames?.product_option_titles.replace('size - ', '').split(' - ')

      // Get the values from the url.
      const arrayOfUrlValues = arrayOfOptionNames.map((optionName) => getValueFromUrl(optionName)).filter((optionName) => optionName)

      if (arrayOfUrlValues && arrayOfUrlValues.length > 0) {
        return returnVariantList(arrayOfUrlValues)
      } else {
        return null
      }
    }

    /**
     * Handle set pricing in size options.
     */
    const handleSetPricingInSizeOptions = () => {
      const connectedProductId = connectedProductSizeFieldsetElement.dataset.connectedProductId
      const fieldsetName = connectedProductSizeFieldsetElement.getAttribute('name')

      if (connectedProductId) {
        handleSetPriceInFieldsetElement(fieldsetName, connectedProductId)
      }
    }

    /**
     * Handle set pricing in size options.
     */
    const handleSetImageInSizeOptions = () => {
      const connectedProductId = connectedProductSizeFieldsetElement.dataset.connectedProductId
      const fieldsetName = connectedProductSizeFieldsetElement.getAttribute('name')

      if (connectedProductId) {
        handleSetImageInSizeFieldsetElements(fieldsetName, connectedProductId)
      }
    }

    /**
     * Set variant id.
     */
    const setVariantId = () => {
      const getAllOptionsNames = allVariants.find((variant) => variant.product_option_titles)
      const arrayOfOptionNames = getAllOptionsNames?.product_option_titles.split(' - ')
      const allOptions = getAllOptions(arrayOfOptionNames)
      const allOptionsString = allOptions.map((option) => Object.values(option)).join(' - ')
      const correctVariant = getCorrectVariantFromOptionsOrNull(allVariants, allOptionsString)

      if (correctVariant) {
        // Set correct variant id in the connected product variant id input.
        variantIdElement.value = correctVariant.id
      }
    }

    const initialLoad = () => {
      handleSetPricingInSizeOptions()
      handleSetImageInSizeOptions()
    }

    /**
     * Load the intials settings, such as option images and prices.
     */
    initialLoad()

    /**
     * Listen for variant option changes.
     */
    listenForVariantOptionsChanges(() => {
      setVariantId()
      handleSetPricingInSizeOptions()
      handleSetImageInSizeOptions()
    })
  }
}

// Check if element is defined, if not define it..
// eslint-disable-next-line no-undefined
if (customElements.get('connected-product') === undefined) {
  customElements.define('connected-product', ConnectedProduct)
}
